/**
 * Derived SU table — read-only matrix beneath the editable forecast
 * grid. Same row set (FacilityCOTs) as MasForecastGrid; each cell is
 * computed live from the editable percentages on the working copy.
 *
 * Formula (per facility, per month):
 *
 *     monthly_SU = (forecast_percentage / 100)
 *                  × Σ over qualifying packs:
 *                        (market_data_row.annual_market / packsize)
 *
 * i.e. each pack's annual EU figure is first converted to SUs by
 * dividing by its pack size, then summed across the qualifying packs
 * for that facility, and finally scaled by the facility's monthly %.
 *
 * Qualifying-pack rule (per product spec):
 *   - MAS         → packs where `selected = true`
 *   - PackoutMAS  → packs where `selected = true && used_in_calc = true`
 *
 * The percentage is applied once at the facility level (it's per-CoT,
 * per-month — not per pack), so it factors out of the pack sum.
 *
 * Why this lives in the UI rather than the API
 * --------------------------------------------
 * The backend stores percentages and per-pack annual market totals —
 * SU is always derivable. Computing here keeps the operator's edits
 * and the SU display in lock-step (no "save first to see SU" UX
 * hiccup).
 *
 * The table renders only when the active slice is MAS or PackoutMAS.
 * The TOTAL row sums each month's SU across facilities so the
 * operator can sanity-check the monthly volume against the annual
 * market total.
 */

import { observer } from 'mobx-react';

import { useEditStore } from './editStoreContext';
import styles from './suPricesTable.module.scss';

// Same partition rule as MasForecastGrid — keeps the two tables in
// lockstep visually. Non-CMOP = MTF / TMOP; CMOP = everything else.
const NON_CMOP_FACILITY_COTS = new Set(['MTF', 'TMOP']);

const SUPricesTable = observer(() => {
  const store = useEditStore();
  const slice = store.activeSlice;
  if (!slice) return null;

  // Same source as MasForecastGrid: working copy if dirty, server otherwise.
  const data = store.workingByNDC.get(slice.ndc) ?? slice.serverData;
  if (data.data.length === 0) return null;

  const months = data.data[0]?.forecast_data.map(m => m.forecast_month) ?? [];

  // Build the qualifying-pack set per the type-specific rule:
  //   MAS         → selected
  //   PackoutMAS  → selected && used_in_calc
  // The NC types never render this table so they don't need a branch.
  const isPackoutMas = slice.type === 'PackoutMAS';
  const qualifyingPacks = new Set(
    data.selected_packsizes
      .filter(p =>
        isPackoutMas ? p.selected && p.used_in_calc : p.selected
      )
      .map(p => p.packsize)
  );

  // Pre-aggregate per facility: Σ (annual_market / packsize) across
  // qualifying packs for that facility. Done once per render so each
  // month cell is a single multiply (× pct / 100) instead of looping
  // the market_data array for every cell.
  const annualSUByFacility = new Map<string, number>();
  for (const row of store.marketData) {
    if (!qualifyingPacks.has(row.packsize)) continue;
    const packSize = parseFloat(row.packsize);
    if (!Number.isFinite(packSize) || packSize <= 0) continue;
    const annualMarketEU = parseFloat(row.annual_market);
    if (!Number.isFinite(annualMarketEU)) continue;
    const su = annualMarketEU / packSize;
    annualSUByFacility.set(
      row.facility_cot,
      (annualSUByFacility.get(row.facility_cot) ?? 0) + su
    );
  }

  // Monthly SU per facility = facility's qualifying-pack SU sum × pct/100.
  // Padded canonical CoTs (no rows in market_data) get 0 here and the
  // cell renders as "–" downstream.
  const cellSU: number[][] = data.data.map(entry => {
    const annualSU = annualSUByFacility.get(entry.facility_cot_agency) ?? 0;
    return entry.forecast_data.map(
      month => (annualSU * month.forecast_percentage) / 100
    );
  });
  // Subtotal / TOTAL sums exclude rows the user has unchecked
  // (is_facility_selected=false) on the MAS grid above. The
  // checkbox lives on MasForecastGrid; SU Prices is a derived
  // mirror so it just respects the same flag.
  const selectedIdxs = data.data
    .map((entry, idx) => (entry.is_facility_selected ? idx : -1))
    .filter(i => i >= 0);
  const monthTotals: number[] = months.map((_, mIdx) =>
    selectedIdxs.reduce((sum, i) => sum + cellSU[i][mIdx], 0)
  );

  // Partition by CMOP membership — same rule as MasForecastGrid.
  const cmopIdxs: number[] = [];
  const nonCmopIdxs: number[] = [];
  data.data.forEach((entry, idx) => {
    if (NON_CMOP_FACILITY_COTS.has(entry.facility_cot_agency)) {
      nonCmopIdxs.push(idx);
    } else {
      cmopIdxs.push(idx);
    }
  });

  // CMOP subtotal sums only checked rows in the CMOP partition.
  const cmopSelectedIdxs = cmopIdxs.filter(
    i => data.data[i].is_facility_selected
  );
  const cmopMonthTotals: number[] = months.map((_, mIdx) =>
    cmopSelectedIdxs.reduce((s, i) => s + cellSU[i][mIdx], 0)
  );

  const showCmopSubtotal = cmopIdxs.length > 0 && nonCmopIdxs.length > 0;

  const renderDataRow = (rIdx: number) => {
    const entry = data.data[rIdx];
    const isSelected = entry.is_facility_selected;
    return (
      <tr
        key={`${entry.facility_cot_agency}-${rIdx}`}
        className={isSelected ? undefined : styles.unselectedRow}
      >
        <td className={`${styles.bodyCell} ${styles.stickyFacility} ${styles.facilityHead}`}>
          <div className={styles.facilityText}>
            <span className={styles.facilityLabel}>{entry.facility_cot_agency}</span>
            {entry.group && (
              <span className={styles.facilityGroup}>{entry.group}</span>
            )}
          </div>
        </td>
        {cellSU[rIdx].map((su, mIdx) => (
          <td
            key={mIdx}
            className={`${styles.bodyCell} ${styles.numericCell} ${
              su <= 0 ? styles.dim : ''
            }`}
          >
            {su > 0 ? formatSU(su) : '–'}
          </td>
        ))}
      </tr>
    );
  };

  return (
    <div className={styles.wrap}>
      <h4 className={styles.heading}>
        SU
        <span className={styles.subheading}>
          (Monthly SU = Σ (Annual Market / Pack Size) × % allocation)
        </span>
      </h4>
      <div className={styles.scrollWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={`${styles.headCell} ${styles.stickyFacilityHead}`}>Facility / COT</th>
              {months.map(m => (
                <th key={m} className={styles.headCell}>
                  {formatMonthLabel(m)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* CMOP group rows */}
            {cmopIdxs.map(renderDataRow)}

            {/* CMOP subtotal — only when both groups have at least one row */}
            {showCmopSubtotal && (
              <tr className={styles.subtotalRow}>
                <td
                  className={`${styles.bodyCell} ${styles.stickyFacility} ${styles.subtotalCell} ${styles.subtotalLabel}`}
                >
                  CMOP Total
                </td>
                {cmopMonthTotals.map((su, mIdx) => (
                  <td
                    key={mIdx}
                    className={`${styles.bodyCell} ${styles.numericCell} ${styles.subtotalCell}`}
                  >
                    {su > 0 ? formatSU(su) : '–'}
                  </td>
                ))}
              </tr>
            )}

            {/* Non-CMOP rows */}
            {nonCmopIdxs.map(renderDataRow)}

            {/* Grand total */}
            <tr className={styles.totalRow}>
              <td className={`${styles.bodyCell} ${styles.stickyFacility} ${styles.totalLabel}`}>
                TOTAL
              </td>
              {monthTotals.map((total, mIdx) => (
                <td
                  key={mIdx}
                  className={`${styles.bodyCell} ${styles.numericCell} ${styles.totalCell}`}
                >
                  {total > 0 ? formatSU(total) : '–'}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
});

function formatMonthLabel(yyyymmdd: string): string {
  const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(yyyymmdd);
  if (!m) return yyyymmdd;
  const year = m[1].slice(2);
  const monthIdx = Math.max(0, Math.min(11, parseInt(m[2], 10) - 1));
  return `${SHORT_MONTHS[monthIdx]} '${year}`;
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatSU(value: number): string {
  if (!Number.isFinite(value)) return '–';
  return Math.round(value).toLocaleString();
}

export default SUPricesTable;
